import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import useAppointmentStore from '../../stores/appointmentStore';
import useVisitStore from '../../stores/visitStore';
import useOrgSettingsStore from '../../stores/orgSettingsStore';
import { usePermission, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import SmartTimeInput from './SmartTimeInput';
import DayTimeline, { effectiveStatus } from './DayTimeline';
import { PhoneButton } from '../common/PhoneButton';
import DatePicker from '../common/DatePicker';
import BlockTimeModal from './BlockTimeModal';
import ConsentBlock, { resolveConsentGranted } from '../common/ConsentBlock';
import { DIGITAL_RECORDS_KEY } from '../../services/api/consentApi';
import { getPatient } from '../../services/api/visitApi';
import { STAFF_ROLES, STAFF_STATUS } from '../../constants/staff';
import useAssignableProviders, { DOCTORS_LOAD_ERROR_COPY } from '../../hooks/useAssignableProviders';
import ScopeCaption, { useDeskWallViewer } from '../common/ScopeCaption';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ManageAppointmentsPage — Dedicated appointment list (today + future)
 *
 * Shows all appointments from today onwards by default.
 * Features:
 *   • Status filter (All, Booked, Checked In, Cancelled, No Show)
 *   • Doctor filter (for Admin/Receptionist)
 *   • Date range filter
 *   • Search by name/mobile
 *   • Actions: Cancel, Reschedule, Rebook, Check In
 *   • Phone number displayed near patient name
 *   • Inline reschedule with SmartTimeInput
 * ═══════════════════════════════════════════════════════════════════════
 */

const STATUS_COLORS = {
  BOOKED:     { bg: 'rgba(59,130,246,0.12)',   text: '#3b82f6',  label: 'Booked' },
  CHECKED_IN: { bg: 'rgba(34,197,94,0.12)',    text: '#22c55e',  label: 'Checked In' },
  IN_PROGRESS:{ bg: 'rgba(79,70,229,0.14)',    text: '#4f46e5',  label: 'In Progress' },
  COMPLETED:  { bg: 'rgba(100,116,139,0.14)',  text: '#64748b',  label: 'Completed' },
  CANCELLED:  { bg: 'rgba(107,114,128,0.12)',  text: '#6b7280',  label: 'Cancelled' },
  NO_SHOW:    { bg: 'rgba(245,158,11,0.12)',   text: '#f59e0b',  label: 'No Show' },
  EXPIRED:    { bg: 'rgba(239,68,68,0.12)',    text: '#ef4444',  label: 'Expired' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'CHECKED_IN', label: 'Checked In' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'NO_SHOW', label: 'No Show' },
];

function todayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

// Next 30-min slot strictly after the current IST time (5:00→5:30, 5:33→6:00).
function nextHalfHourHHMM() {
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const mins = ist.getHours() * 60 + ist.getMinutes();
  let n = Math.ceil((mins + 1) / 30) * 30;
  if (n > 23 * 60 + 30) n = 23 * 60 + 30; // clamp to last 30-min slot of the day
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
}

function formatTime(timeVal) {
  if (!timeVal) return '';
  try {
    const match = /^(\d{1,2}):(\d{2})/.exec(timeVal);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = match[2];
      const period = h >= 12 ? 'PM' : 'AM';
      return `${h % 12 || 12}:${m} ${period}`;
    }
  } catch (_) {}
  return timeVal;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_) {}
  return dateStr;
}

/**
 * Check if the cancellation is within 60 minutes (for rebook eligibility).
 */
function canRebook(appointment) {
  if (appointment.status !== 'CANCELLED') return false;
  const cancelled = appointment.cancelled_at || appointment.updated_at;
  if (!cancelled) return false;
  try {
    const cancelTime = new Date(cancelled);
    const now = new Date();
    return (now - cancelTime) < 60 * 60 * 1000; // 60 minutes
  } catch (_) {}
  return false;
}

export default function ManageAppointmentsPage({ onBack, onSuccess, onCancel }) {
  const canManage = usePermission('MANAGE_APPOINTMENT');
  const { isScoped, memberId } = useScope();
  const deskWalled = useDeskWallViewer();
  const { isDark } = useTheme();
  const { dailyVisits, fetchDailyVisits } = useVisitStore();
  const {
    appointments, appointmentCount, loading, error,
    fetchList, cancel, update, rebook, checkIn, clearError,
    dayBlocks, fetchDoctorBlocks, createBlock, deleteBlock, skipBlock,
  } = useAppointmentStore();

  const [statusFilter, setStatusFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState('');
  const [viewMode, setViewMode] = useState('timeline');   // 'timeline' | 'list' (falls back to list when not eligible)
  const [timelineAppt, setTimelineAppt] = useState(null);  // block tapped in timeline mode
  const [expandedId, setExpandedId] = useState(null);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleTimeRaw, setRescheduleTimeRaw] = useState('');
  const [rescheduleTime24, setRescheduleTime24] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [blockDefaultTime, setBlockDefaultTime] = useState(null);
  const [rangeMode, setRangeMode] = useState(false);

  // ── CONSENT-CAPTURE D14 — check-in is a 6th patient-registering path.
  // Catalogue fetch mirrors RegisterVisitForm/AdmitPatientForm's (same
  // store, single-flight cached — safe alongside their own calls).
  const [checkInTarget, setCheckInTarget] = useState(null); // the appointment being checked in, or null
  const [checkInPatient, setCheckInPatient] = useState(null); // fetched patient row — the REAL consent state (bk-1444)
  const [checkInGranted, setCheckInGranted] = useState(true);
  const consentCatalog = useOrgSettingsStore((s) => s.consentCatalog);
  const fetchConsentCatalog = useOrgSettingsStore((s) => s.fetchConsentCatalog);
  useEffect(() => {
    fetchConsentCatalog?.();
  }, [fetchConsentCatalog]);
  const consentCatalogEntry = (consentCatalog?.consents || []).find((c) => c.key === DIGITAL_RECORDS_KEY) || null;

  // bk-1444 / compliance item 16-N6 — a patient who already consented at an
  // earlier OPD/IPD encounter must read "On file" here, never a blanket
  // "Not asked yet": the appointment's `patient_id` resolves to the SAME
  // patient row every other surface reads. One fetch per modal open (not
  // per keystroke/render); resets to null when the modal closes or a
  // DIFFERENT appointment opens.
  useEffect(() => {
    if (!checkInTarget?.patient_id) { setCheckInPatient(null); return undefined; }
    let alive = true;
    getPatient(checkInTarget.patient_id).then((p) => { if (alive) setCheckInPatient(p); });
    return () => { alive = false; };
  }, [checkInTarget]);
  const checkInDigitalRecordsEntry = checkInPatient?.consents?.[DIGITAL_RECORDS_KEY] || null;

  // Seeding the tick must never CLOBBER a choice the operator has already
  // made. The patient row and the catalogue both arrive asynchronously, so a
  // desk that unticks while they are in flight would otherwise have its
  // refusal silently reverted the moment the row lands (QA, lock-race). The
  // flag resets per check-in target, so the next patient seeds normally.
  const checkInConsentTouched = useRef(false);
  useEffect(() => { checkInConsentTouched.current = false; }, [checkInTarget]);

  useEffect(() => {
    if (!checkInTarget) return;
    if (checkInConsentTouched.current) return;
    setCheckInGranted(resolveConsentGranted(checkInDigitalRecordsEntry, consentCatalogEntry));
  }, [checkInTarget, checkInDigitalRecordsEntry, consentCatalogEntry]);

  // Doctor filter options = the server-scoped assignable list (admission-access
  // S10/S14). A scoped doctor has no filter UI, so they cost no request. The
  // appointment list itself is already scoped by the server — no client re-filter.
  const { providers, error: doctorsError } = useAssignableProviders(isScoped ? null : STAFF_ROLES.DOCTOR);
  const doctors = useMemo(
    () => providers.filter((m) => m.status !== STAFF_STATUS.INACTIVE && m.status !== STAFF_STATUS.DISABLED),
    [providers],
  );

  useEffect(() => {
    const filters = { start_date: startDate || todayISO() };
    if (endDate) filters.end_date = endDate;
    if (statusFilter) filters.status = statusFilter;
    if (doctorFilter) filters.doctor_member_id = doctorFilter;
    fetchList(filters);
  }, [startDate, endDate, statusFilter, doctorFilter, fetchList]);

  // ── Filtered + searched appointments ──────────────────────────────
  const filtered = useMemo(() => {
    let list = appointments || [];
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((a) =>
        (a.patient_name || '').toLowerCase().includes(q) ||
        (a.patient_mobile || '').includes(q) ||
        String(a.appointment_number || '').includes(q)
      );
    }
    // Sort: today first, then future, by time ascending
    return [...list].sort((a, b) => {
      const dateComp = (a.appointment_date || '').localeCompare(b.appointment_date || '');
      if (dateComp !== 0) return dateComp;
      return (a.appointment_time || '').localeCompare(b.appointment_time || '');
    });
  }, [appointments, search]);

  // ── Timeline view: single doctor + single day only ────────────────
  const timelineDate = startDate || todayISO();
  const timelineEligible =
    (!endDate || endDate === startDate) &&
    (isScoped || doctors.length === 1 || !!doctorFilter);
  const showTimeline = viewMode === 'timeline' && timelineEligible;
  // The single doctor in view — owns the calendar blocks shown/created here.
  // A scoped doctor has no doctor-filter UI, so fall back to THEIR own member id
  // (never doctors[0], which would attribute their blocks to the first org doctor).
  const blockDoctorId = doctorFilter || (isScoped ? memberId : (doctors.length ? doctors[0]?.member_id : null));

  // Pull the day's visits to overlay live status (IN_PROGRESS / COMPLETED)
  useEffect(() => {
    if (showTimeline) fetchDailyVisits({ date: timelineDate });
  }, [showTimeline, timelineDate, fetchDailyVisits]);

  // Pull the doctor's calendar blocks for the day (expanded occurrences)
  useEffect(() => {
    if (showTimeline && blockDoctorId) fetchDoctorBlocks(blockDoctorId, timelineDate);
  }, [showTimeline, blockDoctorId, timelineDate, fetchDoctorBlocks]);

  const timelineAppointments = useMemo(() => {
    // The timeline shows ONE doctor's calendar (blockDoctorId). Scope the
    // appointments to that same doctor so a doctor's blocks are never overlaid
    // on another doctor's appointments (which made a block look like it belonged
    // to the wrong doctor).
    const scoped = blockDoctorId
      ? (filtered || []).filter((a) => a.doctor_member_id === blockDoctorId)
      : (filtered || []);
    const appts = scoped.map((a) => {
      let status = effectiveStatus(a);
      if (!a.visit_status && a.status === 'CHECKED_IN' && a.visit_id) {
        const v = (dailyVisits || []).find((x) => (x.id || x.visit_id) === a.visit_id);
        if (v && (v.status === 'IN_PROGRESS' || v.status === 'COMPLETED')) status = v.status;
      }
      return { ...a, status };
    });
    return [...appts, ...(dayBlocks || [])];
  }, [filtered, dayBlocks, dailyVisits, blockDoctorId]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleCancel = useCallback(async (id) => {
    setConfirmCancel(null);
    try { await cancel(id); } catch (_) {}
  }, [cancel]);

  // D14 — check-in is no longer instant: it opens the consent-gated modal
  // below (mirrors the Cancel-confirmation modal already on this page).
  const openCheckIn = useCallback((appt) => setCheckInTarget(appt), []);
  const handleConfirmCheckIn = useCallback(async () => {
    // D15.1 — defence in depth: never confirm while the catalogue couldn't
    // be shown, even if something bypasses the disabled button.
    if (!checkInTarget || !checkInGranted || !consentCatalogEntry) return;
    const id = checkInTarget.appointment_id;
    setCheckInTarget(null);
    try { await checkIn(id, checkInGranted); } catch (_) {}
  }, [checkInTarget, checkInGranted, consentCatalogEntry, checkIn]);

  const handleRebook = useCallback(async (id) => {
    try { await rebook(id); } catch (_) {}
  }, [rebook]);

  const handleReschedule = useCallback(async () => {
    if (!rescheduleId || !rescheduleTime24) return;
    try {
      const updates = { appointment_time: rescheduleTime24 };
      if (rescheduleDate) updates.appointment_date = rescheduleDate;
      await update(rescheduleId, updates);
      setRescheduleId(null);
      setRescheduleTimeRaw('');
      setRescheduleTime24(null);
      setRescheduleDate('');
    } catch (_) {}
  }, [rescheduleId, rescheduleTime24, rescheduleDate, update]);

  const handleTimeResolve = useCallback((display, hour24, time24) => {
    setRescheduleTime24(time24);
  }, []);

  const openReschedule = useCallback((appt) => {
    setRescheduleId(appt.appointment_id);
    setRescheduleTimeRaw('');
    setRescheduleTime24(null);
    setRescheduleDate(appt.appointment_date || todayISO());
  }, []);

  const refreshBlocks = useCallback(() => {
    if (blockDoctorId) fetchDoctorBlocks(blockDoctorId, timelineDate);
  }, [blockDoctorId, timelineDate, fetchDoctorBlocks]);

  // Open the block modal. `time` = a tapped slot (HH:MM); when absent (➕ button)
  // default to the next half-hour after now.
  const openBlockModal = useCallback((time) => {
    setBlockDefaultTime(time || nextHalfHourHHMM());
    setBlockModalOpen(true);
  }, []);

  const handleCreateBlock = useCallback(async (payload) => {
    if (!blockDoctorId) return;
    setBlockBusy(true);
    try {
      await createBlock(blockDoctorId, payload);
      setBlockModalOpen(false);
      refreshBlocks();
    } catch (_) {} finally { setBlockBusy(false); }
  }, [blockDoctorId, createBlock, refreshBlocks]);

  const handleDeleteBlockSeries = useCallback(async (blk) => {
    setBlockBusy(true);
    try { await deleteBlock(blk.doctor_member_id || blockDoctorId, blk.block_id); refreshBlocks(); }
    catch (_) {} finally { setBlockBusy(false); setTimelineAppt(null); }
  }, [deleteBlock, blockDoctorId, refreshBlocks]);

  const handleSkipBlockDay = useCallback(async (blk) => {
    setBlockBusy(true);
    try { await skipBlock(blk.doctor_member_id || blockDoctorId, blk.block_id, blk.occ_date); refreshBlocks(); }
    catch (_) {} finally { setBlockBusy(false); setTimelineAppt(null); }
  }, [skipBlock, blockDoctorId, refreshBlocks]);

  if (!canManage) return null;

  // ── Styles ─────────────────────────────────────────────────────────
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const textPrimary = isDark ? '#f5f5f7' : '#111827';

  const pillBtn = (active) => ({
    padding: '0.3rem 0.625rem',
    borderRadius: '0.5rem',
    border: `1px solid ${active ? (isDark ? '#0d9488' : '#115e59') : borderColor}`,
    background: active ? (isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.08)') : 'transparent',
    color: active ? '#0d9488' : textMuted,
    fontSize: '0.6875rem',
    fontWeight: active ? 600 : 400,
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ width: '100%' }}>
      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📅</span>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.125rem', color: textPrimary }}>
            {isScoped ? 'My Appointments' : 'Manage Appointments'}
          </h2>
          <span style={{
            fontSize: '0.6875rem', fontWeight: 500, color: '#0d9488',
            background: isDark ? 'rgba(13, 148, 136,0.12)' : 'rgba(13, 148, 136,0.06)',
            padding: '0.125rem 0.5rem', borderRadius: '1rem',
          }}>
            {appointmentCount}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* List / Timeline toggle — timeline needs single doctor + single day */}
          {timelineEligible && (
            <div style={{
              display: 'flex', gap: '0.25rem', padding: '0.1875rem',
              borderRadius: '0.625rem',
              background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
            }}>
              {[{ v: 'list', l: '☰ List' }, { v: 'timeline', l: '📆 Timeline' }].map((m) => {
                const on = viewMode === m.v;
                return (
                  <button key={m.v} onClick={() => setViewMode(m.v)} style={{
                    padding: '0.3125rem 0.625rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer',
                    fontSize: '0.6875rem', fontWeight: 600,
                    background: on ? '#0d9488' : (isDark ? '#1e293b' : '#ffffff'),
                    color: on ? '#fff' : (isDark ? '#e2e8f0' : '#334155'),
                  }}>{m.l}</button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* S13 — names the walled receptionist's scope (appointment wall). */}
      <ScopeCaption scoped={deskWalled} className="-mt-2 mb-3" />

      {/* ── Filters ── */}
      <div style={{
        display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center',
        marginBottom: '0.75rem',
      }}>
        {/* Status pills */}
        {STATUS_FILTERS.map((sf) => (
          <button
            key={sf.value}
            onClick={() => setStatusFilter(sf.value)}
            style={pillBtn(statusFilter === sf.value)}
          >
            {sf.label}
          </button>
        ))}

        {/* Doctor filter (non-scoped only) */}
        {!isScoped && doctors.length > 1 && (
          <select
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
            style={{
              padding: '0.3rem 0.5rem', borderRadius: '0.5rem',
              border: `1px solid ${borderColor}`,
              background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
              color: textMuted, fontSize: '0.6875rem', cursor: 'pointer',
            }}
          >
            <option value="">All Doctors</option>
            {(doctors || []).map((d) => (
              <option key={d.member_id} value={d.member_id}>
                {d.display_name || `${d.first_name || ''} ${d.last_name || ''}`.trim()}
              </option>
            ))}
          </select>
        )}
        {!isScoped && doctorsError && (
          <span data-testid="doctor-filter-error" style={{ fontSize: '0.6875rem', color: textMuted }}>{DOCTORS_LOAD_ERROR_COPY}</span>
        )}

        {/* Date — single-day navigator (◀ date ▶) by default; toggle to a range calendar */}
        {rangeMode ? (
          <DatePicker
            mode="range"
            range={{ start: startDate, end: endDate || null }}
            isDark={isDark}
            onChange={({ start, end }) => { setStartDate(start); setEndDate(end || ''); }}
          />
        ) : (
          <DatePicker
            mode="single"
            value={startDate}
            isDark={isDark}
            onChange={(d) => { setStartDate(d); setEndDate(''); }}
          />
        )}
        <button type="button" onClick={() => { setRangeMode((r) => !r); setEndDate(''); }} style={{
          padding: '0.3rem 0.5rem', borderRadius: '0.5rem', cursor: 'pointer',
          border: `1px solid ${borderColor}`,
          background: isDark ? '#1e293b' : '#fff',
          color: textMuted, fontSize: '0.6875rem', fontWeight: 600, whiteSpace: 'nowrap',
        }}>{rangeMode ? '📆 Single day' : '📅 Range'}</button>
      </div>

      {/* ── Search ── */}
      <div style={{ marginBottom: '0.75rem' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, mobile, or appointment #..."
          style={{
            width: '100%', padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${borderColor}`,
            background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
            color: textPrimary, fontSize: '0.8125rem',
            outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
          borderRadius: '0.5rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
          border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: '0.8125rem', color: isDark ? '#fca5a5' : '#991b1b' }}>⚠️ {error}</span>
          <button onClick={clearError} style={{
            background: 'none', border: 'none', color: '#ef4444',
            cursor: 'pointer', fontSize: '0.75rem',
          }}>Dismiss</button>
        </div>
      )}

      {/* ── Timeline view (single doctor + single day) ── */}
      {showTimeline && (
        <>
          <div style={{
            marginBottom: '0.5rem', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: '0.5rem',
          }}>
            <span style={{ fontSize: '0.6875rem', color: textMuted }}>
              {formatDate(timelineDate)} · {filtered.length} appointment{filtered.length !== 1 ? 's' : ''} — tap a block for actions, tap a free slot to block time
            </span>
            {blockDoctorId && (
              <button onClick={() => openBlockModal()} style={{
                padding: '0.3125rem 0.625rem', borderRadius: '0.5rem', cursor: 'pointer',
                border: `1px solid ${isDark ? '#64748b' : '#94a3b8'}`,
                background: isDark ? 'rgba(100,116,139,0.2)' : '#f1f5f9',
                color: isDark ? '#e2e8f0' : '#334155', fontSize: '0.6875rem', fontWeight: 600,
                whiteSpace: 'nowrap', flexShrink: 0,
              }}>➕ New</button>
            )}
          </div>
          <DayTimeline
            appointments={timelineAppointments}
            date={timelineDate}
            isDark={isDark}
            mode="view"
            onSelectAppointment={setTimelineAppt}
            onEmptyTap={blockDoctorId ? (t) => openBlockModal(t) : undefined}
          />
        </>
      )}

      {/* ── Loading ── */}
      {!showTimeline && loading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: textMuted, fontSize: '0.875rem' }}>
          Loading appointments...
        </div>
      )}

      {/* ── Empty state ── */}
      {!showTimeline && !loading && filtered.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '3rem 1rem',
          color: textMuted, fontSize: '0.875rem',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📅</div>
          <p style={{ margin: 0 }}>No appointments found</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem' }}>
            Try adjusting your filters or date range
          </p>
        </div>
      )}

      {/* ── Appointment Cards ── */}
      {!showTimeline && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.map((appt) => {
          const status = STATUS_COLORS[appt.status] || STATUS_COLORS.BOOKED;
          const isExpanded = expandedId === appt.appointment_id;
          const isRescheduling = rescheduleId === appt.appointment_id;
          const isToday = appt.appointment_date === todayISO();
          const rebookable = canRebook(appt);

          return (
            <div
              key={appt.appointment_id}
              style={{
                borderRadius: '0.75rem',
                border: `1px solid ${isExpanded ? (isDark ? '#0d9488' : '#115e59') : borderColor}`,
                background: cardBg,
                overflow: 'hidden',
                transition: 'border-color 0.15s',
              }}
            >
              {/* Main row */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : appt.appointment_id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                }}
              >
                {/* Appointment number */}
                <div style={{
                  width: '2.5rem', height: '2.5rem',
                  borderRadius: '0.5rem',
                  background: isDark ? 'rgba(13, 148, 136,0.12)' : 'rgba(13, 148, 136,0.06)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{
                    fontSize: '0.9375rem', fontWeight: 700,
                    color: '#0d9488', fontFamily: 'monospace',
                  }}>
                    #{appt.appointment_number || '—'}
                  </span>
                </div>

                {/* Patient info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: textPrimary }}>
                      {appt.patient_name || 'Unknown'}
                    </span>
                    {appt.patient_mobile && (
                      <PhoneButton phoneNumber={appt.patient_mobile} isDark={isDark} size="sm" />
                    )}
                    {isToday && (
                      <span style={{
                        fontSize: '0.5625rem', fontWeight: 600, color: '#f59e0b',
                        background: 'rgba(245,158,11,0.1)', padding: '0.0625rem 0.375rem',
                        borderRadius: '0.25rem', textTransform: 'uppercase',
                      }}>Today</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.125rem' }}>
                    <span style={{ fontSize: '0.6875rem', color: textMuted }}>
                      {formatDate(appt.appointment_date)} • {formatTime(appt.appointment_time)}
                    </span>
                    <span style={{ fontSize: '0.6875rem', color: textMuted }}>
                      Dr. {appt.doctor_name || '—'}
                    </span>
                  </div>
                  {/* Type / procedure / duration */}
                  <div style={{ fontSize: '0.625rem', color: textMuted, marginTop: '0.125rem' }}>
                    {appt.appointment_type === 'PROCEDURE'
                      ? (appt.procedure_name ? `Procedure: ${appt.procedure_name}` : 'Procedure')
                      : 'Consultation'}
                    {appt.duration_minutes ? ` · ${appt.duration_minutes} min` : ''}
                    {appt.notes ? '  📝' : ''}
                  </div>
                </div>

                {/* Status badge */}
                <span style={{
                  padding: '0.1875rem 0.5rem',
                  borderRadius: '0.375rem',
                  background: status.bg,
                  color: status.text,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}>
                  {status.label}
                </span>
              </div>

              {/* Expanded actions */}
              {isExpanded && (
                <div style={{
                  padding: '0.625rem 1rem',
                  borderTop: `1px solid ${borderColor}`,
                  display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
                }}>
                  {appt.notes && (
                    <div style={{
                      width: '100%', marginBottom: '0.25rem', fontSize: '0.75rem',
                      color: textPrimary, whiteSpace: 'pre-wrap',
                    }}>
                      📝 {appt.notes}
                    </div>
                  )}
                  {/* Check In (only BOOKED + today) */}
                  {appt.status === 'BOOKED' && isToday && (
                    <ActionButton
                      label="✅ Check In"
                      color="#22c55e"
                      isDark={isDark}
                      onClick={() => openCheckIn(appt)}
                    />
                  )}

                  {/* Reschedule (only BOOKED) */}
                  {appt.status === 'BOOKED' && (
                    <ActionButton
                      label="🕐 Reschedule"
                      color="#3b82f6"
                      isDark={isDark}
                      onClick={() => openReschedule(appt)}
                    />
                  )}

                  {/* Cancel (only BOOKED) */}
                  {appt.status === 'BOOKED' && (
                    <ActionButton
                      label="❌ Cancel"
                      color="#ef4444"
                      isDark={isDark}
                      onClick={() => setConfirmCancel(appt.appointment_id)}
                    />
                  )}

                  {/* Rebook (only CANCELLED within 60 min) */}
                  {rebookable && (
                    <ActionButton
                      label="🔄 Rebook"
                      color="#0d9488"
                      isDark={isDark}
                      onClick={() => handleRebook(appt.appointment_id)}
                    />
                  )}

                  {/* Phone */}
                  {appt.patient_mobile && (
                    <PhoneButton phoneNumber={appt.patient_mobile} isDark={isDark} size="sm" />
                  )}
                </div>
              )}

              {/* Inline reschedule form */}
              {isRescheduling && (
                <div style={{
                  padding: '0.75rem 1rem',
                  borderTop: `1px solid ${borderColor}`,
                  background: isDark ? 'rgba(59,130,246,0.05)' : 'rgba(59,130,246,0.02)',
                }}>
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6' }}>
                    Reschedule Appointment
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <SmartTimeInput
                        value={rescheduleTimeRaw}
                        onChange={setRescheduleTimeRaw}
                        onResolve={handleTimeResolve}
                        isDark={isDark}
                        placeholder="New time..."
                      />
                    </div>
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      min={todayISO()}
                      style={{
                        padding: '0.5rem 0.5rem',
                        borderRadius: '0.5rem',
                        border: `1px solid ${borderColor}`,
                        background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
                        color: textPrimary, fontSize: '0.8125rem',
                      }}
                    />
                    <button
                      onClick={handleReschedule}
                      disabled={!rescheduleTime24}
                      style={{
                        padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                        border: 'none',
                        background: rescheduleTime24 ? '#3b82f6' : '#6b7280',
                        color: '#fff', fontSize: '0.75rem', fontWeight: 600,
                        cursor: rescheduleTime24 ? 'pointer' : 'not-allowed',
                        opacity: rescheduleTime24 ? 1 : 0.5,
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => { setRescheduleId(null); setRescheduleTimeRaw(''); setRescheduleTime24(null); }}
                      style={{
                        padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                        border: `1px solid ${borderColor}`,
                        background: 'transparent', color: textMuted,
                        fontSize: '0.75rem', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* ── Timeline block action sheet ── */}
      {timelineAppt && (
        <div
          onClick={() => setTimelineAppt(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', padding: '1rem',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: '360px', boxSizing: 'border-box', borderRadius: '1rem', overflow: 'hidden',
            background: isDark ? '#1e1e24' : '#fff', border: `1px solid ${borderColor}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${borderColor}` }}>
              {timelineAppt.appointment_type === 'BLOCK' ? (
                <>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: textPrimary }}>
                    {timelineAppt.blocking ? '🔒' : '🔖'} {timelineAppt.label || 'Blocked time'}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: textMuted, marginTop: '0.125rem' }}>
                    {formatDate(timelineAppt.occ_date)} ·{' '}
                    {timelineAppt.all_day ? 'All day' : formatTime(timelineAppt.appointment_time)}
                    {timelineAppt.is_recurring ? ` · 🔁 Recurring (${timelineAppt.repeat === 'WEEKLY' ? 'Weekly' : 'Daily'})` : ''}
                  </div>
                  <div style={{ fontSize: '0.6875rem', marginTop: '0.1875rem', fontWeight: 600, color: timelineAppt.blocking ? '#dc2626' : textMuted }}>
                    {timelineAppt.blocking ? 'Blocking — shown on the booking screen' : 'Calendar note — hidden from booking'}
                  </div>
                </>
              ) : (
                <>
              <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: textPrimary }}>
                #{timelineAppt.appointment_number || '—'} · {timelineAppt.patient_name || 'Unknown'}
              </div>
              <div style={{ fontSize: '0.75rem', color: textMuted, marginTop: '0.125rem' }}>
                {formatDate(timelineAppt.appointment_date)} · {formatTime(timelineAppt.appointment_time)} ·{' '}
                {(timelineAppt.appointment_type === 'PROCEDURE' ? 'Procedure' : 'Consultation')}
                {timelineAppt.duration_minutes ? ` · ${timelineAppt.duration_minutes} min` : ''}
              </div>
                </>
              )}
              {Array.isArray(timelineAppt.procedures) && timelineAppt.procedures.length > 0 && (
                <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.0625rem' }}>
                  {timelineAppt.procedures.map((p, i) => (
                    <div key={p.procedure_id || i} style={{ fontSize: '0.6875rem', color: textMuted }}>
                      • {p.name || 'Procedure'}{p.duration_minutes ? ` · ${p.duration_minutes}m` : ''}
                    </div>
                  ))}
                </div>
              )}
              {timelineAppt.notes && (
                <div style={{ fontSize: '0.75rem', color: textPrimary, marginTop: '0.375rem', whiteSpace: 'pre-wrap' }}>
                  📝 {timelineAppt.notes}
                </div>
              )}
            </div>
            <div style={{ padding: '0.875rem 1.25rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {timelineAppt.appointment_type === 'BLOCK' && timelineAppt.is_recurring && (
                <>
                  <ActionButton label="🗑 Remove just this day" color="#ef4444" isDark={isDark}
                    onClick={() => handleSkipBlockDay(timelineAppt)} />
                  <ActionButton label="🗑 Remove whole series" color="#ef4444" isDark={isDark}
                    onClick={() => handleDeleteBlockSeries(timelineAppt)} />
                </>
              )}
              {timelineAppt.appointment_type === 'BLOCK' && !timelineAppt.is_recurring && (
                <ActionButton label="🗑 Remove block" color="#ef4444" isDark={isDark}
                  onClick={() => handleDeleteBlockSeries(timelineAppt)} />
              )}
              {timelineAppt.appointment_type !== 'BLOCK' && timelineAppt.status === 'BOOKED' && timelineAppt.appointment_date === todayISO() && (
                <ActionButton label="✅ Check In" color="#22c55e" isDark={isDark}
                  onClick={() => { const a = timelineAppt; setTimelineAppt(null); openCheckIn(a); }} />
              )}
              {timelineAppt.status === 'BOOKED' && (
                <ActionButton label="🕐 Reschedule" color="#3b82f6" isDark={isDark}
                  onClick={() => { const a = timelineAppt; setTimelineAppt(null); setViewMode('list'); setExpandedId(a.appointment_id); openReschedule(a); }} />
              )}
              {timelineAppt.status === 'BOOKED' && (
                <ActionButton label="❌ Cancel" color="#ef4444" isDark={isDark}
                  onClick={() => { setConfirmCancel(timelineAppt.appointment_id); setTimelineAppt(null); }} />
              )}
              {canRebook(timelineAppt) && (
                <ActionButton label="🔄 Rebook" color="#0d9488" isDark={isDark}
                  onClick={() => { handleRebook(timelineAppt.appointment_id); setTimelineAppt(null); }} />
              )}
              {timelineAppt.patient_mobile && (
                <PhoneButton phoneNumber={timelineAppt.patient_mobile} isDark={isDark} size="sm" />
              )}
              <button onClick={() => setTimelineAppt(null)} style={{
                marginLeft: 'auto', padding: '0.3rem 0.625rem', borderRadius: '0.375rem',
                border: `1px solid ${borderColor}`, background: 'transparent', color: textMuted,
                fontSize: '0.6875rem', cursor: 'pointer',
              }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Block time modal ── */}
      {blockModalOpen && (
        <BlockTimeModal
          isDark={isDark}
          defaultDate={timelineDate}
          defaultTime={blockDefaultTime}
          busy={blockBusy}
          onSave={handleCreateBlock}
          onClose={() => setBlockModalOpen(false)}
        />
      )}

      {/* ── Cancel Confirmation Modal ── */}
      {confirmCancel && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            width: '90%', maxWidth: '340px',
            borderRadius: '1rem', overflow: 'hidden',
            background: isDark ? '#1e1e24' : '#fff',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            border: `1px solid ${borderColor}`,
            padding: '1.5rem',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: textPrimary }}>
              Cancel Appointment?
            </h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.8125rem', color: textMuted }}>
              This will cancel the appointment. You can rebook within 60 minutes.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setConfirmCancel(null)}
                style={{
                  flex: 1, padding: '0.625rem', borderRadius: '0.5rem',
                  border: `1px solid ${borderColor}`, background: 'transparent',
                  color: textMuted, fontSize: '0.8125rem', cursor: 'pointer',
                }}
              >
                Keep
              </button>
              <button
                onClick={() => handleCancel(confirmCancel)}
                style={{
                  flex: 1, padding: '0.625rem', borderRadius: '0.5rem',
                  border: 'none', background: '#ef4444',
                  color: '#fff', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Cancel Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Check-in consent modal (D14) — check-in registers a visit, so it
          gates on consent exactly like Register Visit / Register Admission /
          the inline register dialog: unticking disables Check In with a
          reason beside the control, never styled as an error on the row. ── */}
      {checkInTarget && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', padding: '1rem',
        }}>
          <div style={{
            width: '100%', maxWidth: '380px', boxSizing: 'border-box',
            borderRadius: '1rem', overflow: 'hidden',
            background: isDark ? '#1e1e24' : '#fff',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            border: `1px solid ${borderColor}`,
            padding: '1.25rem',
          }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: textPrimary }}>
              Check in {checkInTarget.patient_name || 'this patient'}?
            </h3>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: textMuted }}>
              This creates their visit and adds them to today&rsquo;s queue.
            </p>
            <ConsentBlock
              catalogEntry={consentCatalogEntry}
              checked={checkInGranted}
              onChange={(v) => { checkInConsentTouched.current = true; setCheckInGranted(v); }}
              existingEntry={checkInDigitalRecordsEntry}
            />
            {/* D15.1 — a failed/unavailable catalogue GET blocks Check In too,
                regardless of `checkInGranted` (which can be true from an
                EXISTING patient record even while the catalogue is down). */}
            {(() => {
              const checkInDisabled = !checkInGranted || !consentCatalogEntry;
              return (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button
                      onClick={() => setCheckInTarget(null)}
                      style={{
                        flex: 1, padding: '0.625rem', borderRadius: '0.5rem',
                        border: `1px solid ${borderColor}`, background: 'transparent',
                        color: textMuted, fontSize: '0.8125rem', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmCheckIn}
                      disabled={checkInDisabled}
                      data-testid="checkin-confirm"
                      style={{
                        flex: 1, padding: '0.625rem', borderRadius: '0.5rem',
                        border: 'none', background: checkInDisabled ? '#6b7280' : '#22c55e',
                        color: '#fff', fontSize: '0.8125rem', fontWeight: 600,
                        cursor: checkInDisabled ? 'not-allowed' : 'pointer',
                        opacity: checkInDisabled ? 0.6 : 1,
                      }}
                    >
                      ✅ Check In
                    </button>
                  </div>
                  {!consentCatalogEntry ? (
                    <p
                      style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: textMuted }}
                      data-testid="consent-required-reason"
                    >
                      Consent text unavailable — retry.
                    </p>
                  ) : !checkInGranted && (
                    <p
                      style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: textMuted }}
                      data-testid="consent-required-reason"
                    >
                      Consent is required to check in this patient.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Action Button ────────────────────────────────────────────────────

function ActionButton({ label, color, isDark, onClick }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        padding: '0.3rem 0.625rem',
        borderRadius: '0.375rem',
        border: `1px solid ${color}22`,
        background: `${color}15`,
        color: color,
        fontSize: '0.6875rem',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'background 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}
